import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, PieChart, Pie
} from 'recharts';
import { TrendingUp, TrendingDown, Users, MessageSquare, Filter, ChevronDown, ChevronUp, Search, Download, Link, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { secureExportDownload } from '../utils/secureExport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Colores consistentes
const COLORS = {
  promoter: '#22c55e',
  passive: '#eab308',
  detractor: '#ef4444',
  primary: '#3b82f6',
  secondary: '#8b5cf6',
  muted: '#6b7280',
  contenido: '#3b82f6',
  plataforma: '#8b5cf6',
  servicio: '#06b6d4',
  empleabilidad: '#f59e0b',
  lealtad: '#22c55e',
};

const EJE_LABELS = {
  contenido: 'Contenido',
  plataforma: 'Plataforma',
  servicio: 'Servicio',
  empleabilidad: 'Empleabilidad',
  lealtad: 'Lealtad',
};

// Componente NPS Gauge simplificado
const NPSGauge = ({ score, size = 'large' }) => {
  const getColor = (s) => {
    if (s >= 50) return COLORS.promoter;
    if (s >= 0) return COLORS.passive;
    return COLORS.detractor;
  };
  
  const isLarge = size === 'large';
  
  return (
    <div className={`flex flex-col items-center ${isLarge ? 'p-6' : 'p-3'}`}>
      <div 
        className={`${isLarge ? 'text-6xl' : 'text-3xl'} font-bold`}
        style={{ color: getColor(score) }}
      >
        {score >= 0 ? '+' : ''}{score}
      </div>
      <div className={`${isLarge ? 'text-lg' : 'text-sm'} text-gray-500 mt-1`}>NPS Score</div>
    </div>
  );
};

// Card de Eje con Top-2-Box
const EjeCard = ({ name, data, color }) => {
  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow p-4 border-l-4" style={{ borderColor: color }}>
        <div className="text-sm font-medium text-gray-700 mb-2">{name}</div>
        <div className="text-2xl font-bold text-gray-400">N/A</div>
        <div className="text-xs text-gray-400">Sin datos</div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow p-4 border-l-4" style={{ borderColor: color }}>
      <div className="text-sm font-medium text-gray-700 mb-2">{name}</div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold" style={{ color }}>{data.top2_pct}%</span>
        <span className="text-xs text-gray-500">Top-2</span>
      </div>
      <div className="mt-2 flex justify-between text-xs text-gray-500">
        <span>Prom: {data.avg}</span>
        <span>n={data.n.toLocaleString()}</span>
      </div>
      {/* Mini barra de progreso */}
      <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all"
          style={{ width: `${data.top2_pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

// Componente principal NPS360 Dashboard
const NPS360Dashboard = ({ selectedPartnerName, globalMode, getAuthHeaders, onRelink }) => {
  const [activeView, setActiveView] = useState('summary');
  
  // Inicializar filtro de instancia según props
  const initialInstance = globalMode ? 'all' : (selectedPartnerName ? selectedPartnerName.toUpperCase() : 'all');
  
  const [filters, setFilters] = useState({
    instance: initialInstance,
    period_start: null,
    period_end: null,
    nps_group: 'all',
    section_month: 'all',  // Filtro por sección específica (YYYY-MM-01)
  });
  const [filterOptions, setFilterOptions] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [evolutionData, setEvolutionData] = useState(null);
  const [driversData, setDriversData] = useState(null);
  const [voiceData, setVoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [commentSearch, setCommentSearch] = useState('');
  const [commentPage, setCommentPage] = useState(1);

  // Sincronizar instancia con el partner global seleccionado
  useEffect(() => {
    const newInstance = globalMode ? 'all' : (selectedPartnerName ? selectedPartnerName.toUpperCase() : 'all');
    setFilters(f => {
      if (f.instance !== newInstance) {
        // Resetear section_month al cambiar instancia (las secciones cambian)
        return { ...f, instance: newInstance, section_month: 'all' };
      }
      return f;
    });
  }, [selectedPartnerName, globalMode]);

  // Cargar opciones de filtros (scoped por instancia seleccionada)
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const headers = getAuthHeaders ? getAuthHeaders() : {};
        // Pasar la instancia seleccionada para que las secciones estén scoped
        const instanceParam = filters.instance && filters.instance !== 'all' ? `?instance=${encodeURIComponent(filters.instance)}` : '';
        const res = await fetch(`${API_URL}/api/nps360/filters${instanceParam}`, { headers });
        if (!res.ok) {
          console.error('Error loading filters:', res.status);
          return;
        }
        const data = await res.json();
        setFilterOptions(data);
        // Reset section_month cuando cambia la instancia (las secciones ya no aplican)
      } catch (err) {
        console.error('Error loading filters:', err);
      }
    };
    loadFilters();
  }, [getAuthHeaders, filters.instance]); // Re-cargar cuando cambia la instancia

  // Construir query string de filtros
  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.instance && filters.instance !== 'all') {
      params.append('instance', filters.instance);
    }
    if (filters.period_start) params.append('period_start', filters.period_start);
    if (filters.period_end) params.append('period_end', filters.period_end);
    if (filters.nps_group && filters.nps_group !== 'all') {
      params.append('nps_group', filters.nps_group);
    }
    // Filtro por sección específica (mes del ciclo académico)
    if (filters.section_month && filters.section_month !== 'all') {
      params.append('section_month', filters.section_month);
    }
    return params.toString();
  }, [filters]);

  // Exportar detractores (respetando filtros actuales)
  const handleExportDetractors = async () => {
    try {
      toast.info("Preparando descarga de detractores...");
      
      // Construir URL con filtros actuales
      let url = `${API_URL}/api/nps/export-detractors?`;
      const params = new URLSearchParams();
      
      if (filters.instance && filters.instance !== 'all') {
        params.append('instance_name', filters.instance);
      }
      if (filters.period_start) {
        params.append('period_start', filters.period_start);
      }
      if (filters.period_end) {
        params.append('period_end', filters.period_end);
      }
      
      url += params.toString();
      
      const filename = `detractores_nps_${filters.instance || 'all'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      await secureExportDownload({ url, filename });
      toast.success("Excel de detractores descargado");
    } catch (err) {
      if (err.message !== 'cancelado') {
        console.error("Export Detractors error:", err);
        toast.error("Error al descargar: " + (err.message || "Error desconocido"));
      }
    }
  };

  // Re-vincular registros NPS
  const handleRelink = async () => {
    try {
      toast.info("Re-vinculando registros NPS...");
      const response = await fetch(`${API_URL}/api/nps/relink`, {
        method: 'POST',
        headers: getAuthHeaders ? getAuthHeaders() : {}
      });
      
      if (!response.ok) throw new Error('Error al re-vincular');
      
      const result = await response.json();
      toast.success(`Re-vinculados ${result.relinked_total || 0} registros`);
      
      // Recargar datos
      if (onRelink) onRelink();
      setLoading(true);
    } catch (err) {
      console.error("Relink error:", err);
      toast.error("Error al re-vincular: " + (err.message || "Error desconocido"));
    }
  };

  // Cargar datos según vista activa
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const query = buildQuery();
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      
      try {
        if (activeView === 'summary' || activeView === 'all') {
          const res = await fetch(`${API_URL}/api/nps360/summary?${query}`, { headers });
          if (res.ok) setSummaryData(await res.json());
        }
        
        if (activeView === 'evolution' || activeView === 'all') {
          const res = await fetch(`${API_URL}/api/nps360/evolution?${query}`, { headers });
          if (res.ok) setEvolutionData(await res.json());
        }
        
        if (activeView === 'drivers' || activeView === 'all') {
          const res = await fetch(`${API_URL}/api/nps360/drivers?${query}`, { headers });
          if (res.ok) setDriversData(await res.json());
        }
        
        if (activeView === 'voice' || activeView === 'all') {
          const voiceQuery = new URLSearchParams(query);
          voiceQuery.append('page', commentPage);
          voiceQuery.append('page_size', 10);
          if (commentSearch) voiceQuery.append('search', commentSearch);
          const res = await fetch(`${API_URL}/api/nps360/voice?${voiceQuery.toString()}`, { headers });
          if (res.ok) setVoiceData(await res.json());
        }
      } catch (err) {
        console.error('Error loading NPS360 data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [activeView, buildQuery, commentPage, commentSearch, getAuthHeaders]);

  // Vista Resumen
  const renderSummary = () => {
    if (!summaryData) return null;
    
    const { nps, ejes } = summaryData;
    
    return (
      <div className="space-y-6">
        {/* Header con NPS principal */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* NPS Score principal */}
            <div className="md:col-span-1 flex flex-col items-center justify-center border-r border-gray-200">
              <NPSGauge score={nps.score} />
              <div className="text-sm text-gray-500 mt-2">
                {nps.total.toLocaleString()} respuestas
              </div>
            </div>
            
            {/* Distribución NPS */}
            <div className="md:col-span-3 grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-600">{nps.pct_promoters}%</div>
                <div className="text-sm text-green-700">Promotores</div>
                <div className="text-xs text-gray-500">{nps.promoters.toLocaleString()}</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-3xl font-bold text-yellow-600">{nps.pct_passives}%</div>
                <div className="text-sm text-yellow-700">Pasivos</div>
                <div className="text-xs text-gray-500">{nps.passives.toLocaleString()}</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-3xl font-bold text-red-600">{nps.pct_detractors}%</div>
                <div className="text-sm text-red-700">Detractores</div>
                <div className="text-xs text-gray-500">{nps.detractors.toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Ejes de satisfacción */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Ejes de Satisfacción (% Top-2-Box)</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <EjeCard name="Contenido" data={ejes.contenido} color={COLORS.contenido} />
            <EjeCard name="Plataforma" data={ejes.plataforma} color={COLORS.plataforma} />
            <EjeCard name="Servicio" data={ejes.servicio} color={COLORS.servicio} />
            <EjeCard name="Empleabilidad" data={ejes.empleabilidad} color={COLORS.empleabilidad} />
            <EjeCard name="Lealtad" data={ejes.lealtad} color={COLORS.lealtad} />
          </div>
        </div>
      </div>
    );
  };

  // Vista Evolución
  const renderEvolution = () => {
    if (!evolutionData || !evolutionData.months) return null;
    
    const { months, nps, contenido, plataforma, servicio, empleabilidad, lealtad } = evolutionData;
    
    // Preparar datos para el gráfico
    const chartData = months.map(month => ({
      month: month.substring(0, 7), // YYYY-MM
      nps: nps[month]?.nps || null,
      contenido: contenido[month]?.avg || null,
      plataforma: plataforma[month]?.avg || null,
      servicio: servicio[month]?.avg || null,
      empleabilidad: empleabilidad[month]?.avg || null,
      lealtad: lealtad[month]?.avg || null,
    }));
    
    return (
      <div className="space-y-6">
        {/* Gráfico NPS */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Evolución NPS por Mes</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis domain={[-100, 100]} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Line 
                type="monotone" 
                dataKey="nps" 
                stroke={COLORS.primary} 
                strokeWidth={3}
                dot={{ fill: COLORS.primary, strokeWidth: 2 }}
                name="NPS"
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* Gráfico Dimensiones */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Evolución Dimensiones (Promedio 1-5)</h3>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis domain={[1, 5]} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="contenido" stroke={COLORS.contenido} name="Contenido" connectNulls strokeWidth={2} />
              <Line type="monotone" dataKey="plataforma" stroke={COLORS.plataforma} name="Plataforma" connectNulls strokeWidth={2} />
              <Line type="monotone" dataKey="servicio" stroke={COLORS.servicio} name="Servicio" connectNulls strokeWidth={2} />
              <Line type="monotone" dataKey="empleabilidad" stroke={COLORS.empleabilidad} name="Empleabilidad" connectNulls strokeWidth={2} />
              <Line type="monotone" dataKey="lealtad" stroke={COLORS.lealtad} name="Lealtad" connectNulls strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Vista Drivers
  const renderDrivers = () => {
    if (!driversData || !driversData.drivers) return null;
    
    const { drivers } = driversData;
    
    // Preparar datos para el gráfico
    const chartData = drivers.filter(d => d.gap !== null).map(d => ({
      dimension: d.dimension,
      promoters: d.promoters_avg,
      detractors: d.detractors_avg,
      gap: d.gap,
    }));
    
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Drivers del NPS</h3>
          <p className="text-sm text-gray-500 mb-4">
            Diferencia entre promedio de Promotores vs Detractores. Mayor brecha = mayor impacto en NPS.
          </p>
          
          {/* Gráfico de barras agrupadas */}
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 100 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 5]} />
              <YAxis type="category" dataKey="dimension" tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="promoters" fill={COLORS.promoter} name="Promotores" />
              <Bar dataKey="detractors" fill={COLORS.detractor} name="Detractores" />
            </BarChart>
          </ResponsiveContainer>
          
          {/* Tabla de gaps */}
          <div className="mt-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">Dimensión</th>
                  <th className="text-right py-2 px-3">Promotores</th>
                  <th className="text-right py-2 px-3">Detractores</th>
                  <th className="text-right py-2 px-3">Brecha</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((d, idx) => (
                  <tr key={d.field} className={idx === 0 ? 'bg-blue-50' : ''}>
                    <td className="py-2 px-3 font-medium">
                      {d.dimension}
                      {idx === 0 && <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-0.5 rounded">Driver #1</span>}
                    </td>
                    <td className="text-right py-2 px-3 text-green-600">{d.promoters_avg ?? 'N/A'}</td>
                    <td className="text-right py-2 px-3 text-red-600">{d.detractors_avg ?? 'N/A'}</td>
                    <td className="text-right py-2 px-3 font-bold">{d.gap !== null ? `+${d.gap}` : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Vista Voz del Alumno
  const renderVoice = () => {
    if (!voiceData) return null;
    
    const { motivos, necesito_contactar, comments } = voiceData;
    
    // Colores para motivos
    const motivoColors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#22c55e', '#ef4444'];
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Motivo de elección */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Motivo de Elección</h3>
            <div className="space-y-3">
              {motivos.map((m, idx) => {
                const total = motivos.reduce((a, b) => a + b.count, 0);
                const pct = ((m.count / total) * 100).toFixed(1);
                return (
                  <div key={m.motivo} className="flex items-center gap-3">
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: motivoColors[idx % motivoColors.length] }}
                    />
                    <div className="flex-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700 truncate">{m.motivo}</span>
                        <span className="text-gray-500 ml-2">{pct}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full mt-1">
                        <div 
                          className="h-full rounded-full"
                          style={{ 
                            width: `${pct}%`, 
                            backgroundColor: motivoColors[idx % motivoColors.length] 
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 w-12 text-right">{m.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Necesito Contactar */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">¿Necesita Contacto?</h3>
            <div className="flex items-center justify-center">
              <div className="relative w-48 h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Sí', value: necesito_contactar.si },
                        { name: 'No', value: necesito_contactar.total - necesito_contactar.si }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      <Cell fill="#ef4444" />
                      <Cell fill="#22c55e" />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-bold text-red-600">{necesito_contactar.pct_si}%</span>
                  <span className="text-xs text-gray-500">quiere contacto</span>
                </div>
              </div>
            </div>
            <div className="mt-4 text-center text-sm text-gray-500">
              {necesito_contactar.si} de {necesito_contactar.total} respuestas
            </div>
          </div>
        </div>
        
        {/* Comentarios */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Comentarios ({comments.total})</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar..."
                value={commentSearch}
                onChange={(e) => {
                  setCommentSearch(e.target.value);
                  setCommentPage(1);
                }}
                className="pl-9 pr-4 py-2 border rounded-lg text-sm w-64"
              />
            </div>
          </div>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {comments.data.map((c, idx) => {
              // El backend serializa NaN de pandas como el string "nan" - tratarlo como vacío
              const isValidText = (v) => v && String(v).trim() !== '' && String(v).toLowerCase() !== 'nan';
              const email = isValidText(c.email) ? c.email : null;
              const respondentName = isValidText(c.respondent_name) ? c.respondent_name : null;
              return (
              <div key={idx} className="border-b pb-4 last:border-b-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    c.nps_group === 'Promoter' ? 'bg-green-100 text-green-700' :
                    c.nps_group === 'Detractor' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {c.score} - {c.nps_group}
                  </span>
                  <span className="text-xs text-gray-500">{c.instance_name}</span>
                  {c.section_month && (
                    <span className="text-xs text-gray-400">{c.section_month.substring(0, 7)}</span>
                  )}
                </div>
                {(email || respondentName) && (
                  <p className="text-xs text-gray-500 mb-2" data-testid={`voice-comment-student-${idx}`}>
                    {respondentName && <span className="font-medium text-gray-600">{respondentName}</span>}
                    {respondentName && email && <span className="mx-1">·</span>}
                    {email && <span>{email}</span>}
                  </p>
                )}
                {c.comment && (
                  <p className="text-sm text-gray-700 mb-1">
                    <strong className="text-gray-500">Valora:</strong> {c.comment}
                  </p>
                )}
                {c.improvements && (
                  <p className="text-sm text-gray-700">
                    <strong className="text-gray-500">Mejoras:</strong> {c.improvements}
                  </p>
                )}
                {c.curso && (
                  <p className="text-xs text-gray-400 mt-1">Curso: {c.curso}</p>
                )}
              </div>
              );
            })}
          </div>
          
          {/* Paginación */}
          {comments.total_pages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t">
              <button
                onClick={() => setCommentPage(p => Math.max(1, p - 1))}
                disabled={commentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-500">
                Página {commentPage} de {comments.total_pages}
              </span>
              <button
                onClick={() => setCommentPage(p => Math.min(comments.total_pages, p + 1))}
                disabled={commentPage === comments.total_pages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="nps360-dashboard">
      {/* Header con filtros */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">NPS 360</h2>
            <p className="text-sm text-gray-500">
              Análisis integral de satisfacción
              {filters.instance && filters.instance !== 'all' && (
                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                  {filters.instance}
                </span>
              )}
              {(!filters.instance || filters.instance === 'all') && (
                <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                  Todas las instancias
                </span>
              )}
            </p>
          </div>
          
          {/* Botones de acción */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportDetractors}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              data-testid="nps360-export-detractors-btn"
            >
              <Download className="w-4 h-4" />
              Exportar detractores
            </button>
            <button
              onClick={handleRelink}
              className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="nps360-relink-btn"
            >
              <Link className="w-4 h-4" />
              Re-vincular
            </button>
          </div>
          
          {/* Navegación de vistas */}
          <div className="flex gap-2">
            {[
              { id: 'summary', label: 'Resumen' },
              { id: 'evolution', label: 'Evolución' },
              { id: 'drivers', label: 'Drivers' },
              { id: 'voice', label: 'Voz del Alumno' },
            ].map(view => (
              <button
                key={view.id}
                onClick={() => setActiveView(view.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeView === view.id
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`nps360-tab-${view.id}`}
              >
                {view.label}
              </button>
            ))}
          </div>
          
          {/* Botón de filtros adicionales */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm"
          >
            <Filter className="w-4 h-4" />
            Más filtros
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
        
        {/* Panel de filtros adicionales (período y grupo NPS) */}
        {showFilters && filterOptions && (
          <div className="mt-4 pt-4 border-t">
            {/* Nota explicativa sobre el mes de sección */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>ℹ️ Nota:</strong> Los filtros de fecha corresponden al <strong>Mes de la Sección</strong> (mes del ciclo académico), 
                no a la fecha en que se respondió la encuesta. La encuesta se toma a mes vencido.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Selector de Sección específica */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sección (Mes del ciclo)
                </label>
                <select
                  value={filters.section_month}
                  onChange={(e) => setFilters(f => ({ ...f, section_month: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="nps-section-filter"
                >
                  <option value="all">Todas las secciones</option>
                  {filterOptions.available_sections?.map(section => (
                    <option key={section} value={section}>
                      {new Date(section + 'T00:00:00').toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Desde (Sección)</label>
                <input
                  type="month"
                  value={filters.period_start?.substring(0, 7) || ''}
                  onChange={(e) => setFilters(f => ({ ...f, period_start: e.target.value ? `${e.target.value}-01` : null }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hasta (Sección)</label>
                <input
                  type="month"
                  value={filters.period_end?.substring(0, 7) || ''}
                  onChange={(e) => setFilters(f => ({ ...f, period_end: e.target.value ? `${e.target.value}-01` : null }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grupo NPS</label>
                <select
                  value={filters.nps_group}
                  onChange={(e) => setFilters(f => ({ ...f, nps_group: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="all">Todos</option>
                  <option value="Promoter">Promotores</option>
                  <option value="Passive">Pasivos</option>
                  <option value="Detractor">Detractores</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Contenido según vista activa */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {activeView === 'summary' && renderSummary()}
          {activeView === 'evolution' && renderEvolution()}
          {activeView === 'drivers' && renderDrivers()}
          {activeView === 'voice' && renderVoice()}
        </>
      )}
    </div>
  );
};

export default NPS360Dashboard;
